import { useContext } from "react";
import { useDispatch } from "react-redux";
import { Box, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { PlannerContext } from "../../../context/planner/PlannerContext";
import PlannerTaskItem from "./PlannerTaskItem";
import { setActiveTask, removeTask } from "../../../redux/actions/tasks";
import { setOverlayVisible } from "../../../redux/actions/overlay";
import useTaskAPIClient from "../../../api/task/hooks/useTaskAPIClient";
import removeNotificationsForTask from "../../../notifications/removeNotificationsForTask";
import { Capacitor } from "@capacitor/core";

const TaskBlock = ({ block, isSelected = false, onClick, isPast = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tasks: allTasks, updateBlock } = useContext(PlannerContext);
  const { completeTask } = useTaskAPIClient();
  const { taskIds = [] } = block;

  // Get task objects from IDs
  const blockTasks = taskIds
    .map((id) => allTasks.find((t) => t.id === id))
    .filter(Boolean);

  const handleTaskComplete = (task) => {
    const onConfirmCallback = () => {
      dispatch(removeTask(task.id));
      completeTask(task.id).then(() => {
        if (Capacitor.isNativePlatform()) {
          removeNotificationsForTask(task.id);
        }
      });
      dispatch(setOverlayVisible({ visible: false }));
    };

    dispatch(
      setOverlayVisible({
        visible: true,
        overlayType: "confirm",
        confirmationText: "Have you completed this task?",
        confirmCallback: onConfirmCallback,
      }),
    );
  };

  const handleTaskEdit = (task) => {
    dispatch(setActiveTask(task.id));
    navigate("/edit-task?from=planner");
  };

  const handleTaskRemove = (task) => {
    // Remove task from block by filtering out the task ID
    const updatedTaskIds = taskIds.filter((id) => id !== task.id);
    updateBlock(block.id, {
      ...block,
      taskIds: updatedTaskIds,
    });
  };

  const handleClick = isPast ? undefined : onClick;

  // Stop propagation on task interactions so they work even when block is past
  // But allow propagation when empty (to allow "Add tasks" to trigger block selection)
  // Also allow propagation when clicking the add button
  const handleTaskAreaClick = (e) => {
    if (blockTasks.length > 0) {
      const isAddButton = e.target.closest(".task-block__add-button");
      if (!isAddButton) {
        e.stopPropagation();
      }
    }
  };

  return (
    <Box
      className={`block task-block ${isSelected ? "block--selected" : ""} ${isPast ? "block--past" : ""}`}
      onClick={handleClick}
      sx={isPast ? { cursor: "default" } : {}}
    >
      <Box className="task-block__header">
        <Typography variant="h4" className="task-block__header-text">
          Tasks
        </Typography>
        <Box className="block_time-badge block_time-badge--small">
          <Typography variant="caption" className="block_time-badge-text">
            {block.startTime} — {block.endTime}
          </Typography>
        </Box>
      </Box>
      <Box className="task-block__content" onClick={handleTaskAreaClick}>
        {blockTasks.length > 0 ? (
          <Box className="task-block__task-list">
            {blockTasks.map((task) => (
              <PlannerTaskItem
                key={task.id}
                task={task}
                onComplete={handleTaskComplete}
                onEdit={handleTaskEdit}
                onRemove={handleTaskRemove}
                isPast={isPast}
              />
            ))}
            {!isPast && (
              <Box className="task-block__add-button" title="Add a task">
                <AddIcon className="task-block__add-icon" />
              </Box>
            )}
          </Box>
        ) : !isPast ? (
          <Box className="task-block__add-button" title="Add a task">
            <AddIcon className="task-block__add-icon" />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default TaskBlock;
